import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { useSelector } from "react-redux";
import { LegacyOrganizationAccount } from "../api/types/Account";
import { LegacyVerifyEmailOtpResponseSuccess } from "../api/types/Onboarding";
import { TLegacyUser, TLegacyUserWithAccount } from "../api/types/User";
import { APP_CONFIG } from "../constants/constants";
import { localStorageKeys, useLocalStorage } from "../utils/localStorageItems";
import { useEffect, useState } from "react";

const userTZOverridesAccountTZ = false;

export const convertToTZForDateFnsNoHook = (tz: string) => {
  return tz === "GMT"
    ? "UTC"
    : tz.indexOf("GMT") > -1
      ? tz.replace("GMT", "")
      : tz;
};

export const getAccountTimezoneNoHook = (
  accountData?: LegacyOrganizationAccount
): string => {
  // Try to get timezone from local storage first
  const localStorageTimezone = (() => {
    try {
      const lsItem = window.localStorage.getItem(localStorageKeys.user);
      const lsUser = !!lsItem
        ? (JSON.parse(lsItem) as TLegacyUserWithAccount)
        : undefined;
      if (!!lsUser && !!lsUser?.account?.timezone) {
        return lsUser?.account?.timezone;
      }
    } catch {
      // ignore errors
    }
    return undefined;
  })();

  return (
    localStorageTimezone || accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE
  );
};

export const useAccountTimezone = () => {
  const [tzReady, setTzReady] = useState(false);

  const { getLocalStorageItem } = useLocalStorage();

  // retrieve account data from redux store
  const accountData = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );
  const accountTZForDateFns = useSelector(
    (state: { commonReducer: { timezoneForDateFns: string } }) =>
      state.commonReducer.timezoneForDateFns
  );

  // retrieve user timezone from redux store
  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );
  const loggedInUser: TLegacyUser =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);

  const convertToTZForDateFns = convertToTZForDateFnsNoHook;

  useEffect(() => {
    if (!!loggedInUser?.timezone) {
      setTzReady(true);
    }
  }, [loggedInUser?.timezone]);

  const tzForDisplay =
    (userTZOverridesAccountTZ && loggedInUser?.timezone
      ? loggedInUser.timezone
      : accountData?.timezone) || APP_CONFIG.DEFAULT_TIMEZONE;

  const tzForDateFns =
    userTZOverridesAccountTZ && loggedInUser?.timezone
      ? convertToTZForDateFns(loggedInUser.timezone)
      : accountTZForDateFns ||
        accountData?.timezone ||
        APP_CONFIG.DEFAULT_TIMEZONE;

  return {
    convertToTZForDateFns,
    userTzIsApplicable: userTZOverridesAccountTZ && loggedInUser?.timezone,
    userTzForDisplay: loggedInUser?.timezone,
    accountTzForDisplay: accountData?.timezone,
    tzForDisplay,
    tzForDateFns,
    tzReady,
  };
};

// convert 2023-12-28T21:00 from specific timezone to UTC
export const datepickerValueToUTC = (
  datepickerValue: string,
  timezone: string
) => {
  const selectedDate = new Date(datepickerValue);
  const utcDate = fromZonedTime(
    selectedDate,
    convertToTZForDateFnsNoHook(timezone)
  );
  const jsTime = new Date(utcDate).getTime();
  return jsTime / 1000;
  // co-pilot generated
  // const date = new Date(datepickerValue);
  // const offset = date.getTimezoneOffset();
  // const utcDate = new Date(date.getTime() + offset * 60 * 1000);
  // return utcDate;
};

// convert UTC to 2023-12-28T21:00 in specific timezone
export const timeInUTCToDatepickerValue = (
  timeInUTC: number,
  timezone: string
) => {
  const date = new Date(timeInUTC * 1000);
  return toZonedTime(date, convertToTZForDateFnsNoHook(timezone));
};
